import { MixpanelEvents } from "@/constants/mixpanel-events";
import { trackMixpanelEvent } from "@/utils/mixpanel";
import { useIonViewDidLeave, useIonViewWillEnter } from "@ionic/react";
import { differenceInCalendarDays, isBefore, isValid, parseISO, startOfDay } from "date-fns";
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";

export type MyPlanEntrySource = "app_foreground" | "bottom_nav" | "in_app_navigation";

export type MyPlanInteractionType =
  | "add_meal_opened"
  | "day_selected"
  | "meal_card_opened"
  | "meal_coach_opened"
  | "remove_meal_started";

type AnalyticsProperties = Record<string, string | number | boolean>;

export type MyPlanInteractionTracker = (
  interactionType: MyPlanInteractionType,
  properties?: AnalyticsProperties,
) => void;

interface MyPlanAnalyticsContextValue {
  trackInteraction: MyPlanInteractionTracker;
}

interface MyPlanAnalyticsProps {
  selectedDay: string;
  selectedDateKey: string;
}

interface MyPlanVisit {
  activeDurationMs: number;
  activeStartedAt: number;
  entrySource: MyPlanEntrySource;
  interactionCount: number;
  visitId: string;
}

let pendingMyPlanEntrySource: MyPlanEntrySource | null = null;

/**
 * Marks the source for the next My Plan visit. The bottom navigation is the
 * only source that can be identified reliably before the route is entered.
 */
export const setNextMyPlanEntrySource = (source: MyPlanEntrySource) => {
  pendingMyPlanEntrySource = source;
};

export const getMyPlanDaySelectionProperties = (
  day: string,
  dateKey: string,
  currentDate = new Date(),
): AnalyticsProperties => {
  const date = parseISO(dateKey);
  const today = startOfDay(currentDate);
  const isDateValid = isValid(date);
  const normalizedDate = isDateValid ? startOfDay(date) : today;

  return {
    selected_day: day,
    selected_date: dateKey,
    is_past_day: isDateValid && isBefore(normalizedDate, today),
    days_from_today: differenceInCalendarDays(normalizedDate, today),
  };
};

const consumeMyPlanEntrySource = (): MyPlanEntrySource => {
  const source = pendingMyPlanEntrySource ?? "in_app_navigation";
  pendingMyPlanEntrySource = null;
  return source;
};

const createVisitId = () => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const MyPlanAnalyticsContext = createContext<MyPlanAnalyticsContextValue | null>(null);

/**
 * Tracks an active My Plan visit. Ionic keeps route components mounted in its
 * navigation stack, so the Ionic enter/leave hooks are required rather than
 * relying on React unmount alone.
 */
export const useMyPlanAnalytics = ({ selectedDay, selectedDateKey }: MyPlanAnalyticsProps) => {
  const contextRef = useRef({ selectedDay, selectedDateKey });
  const isViewActiveRef = useRef(false);
  const visitRef = useRef<MyPlanVisit | null>(null);

  contextRef.current = { selectedDay, selectedDateKey };

  const endVisit = useCallback((endReason: "app_backgrounded" | "route_left" | "unmounted") => {
    const visit = visitRef.current;
    if (!visit) return;

    visit.activeDurationMs += Math.max(0, Date.now() - visit.activeStartedAt);
    visitRef.current = null;

    trackMixpanelEvent(MixpanelEvents.MY_PLAN_ENGAGEMENT_ENDED, {
      visit_id: visit.visitId,
      entry_source: visit.entrySource,
      active_duration_seconds: Math.round(visit.activeDurationMs / 1000),
      interaction_count: visit.interactionCount,
      end_reason: endReason,
    });
  }, []);

  const startVisit = useCallback((entrySource?: MyPlanEntrySource) => {
    if (visitRef.current || document.visibilityState === "hidden") return;

    const context = contextRef.current;
    const visit: MyPlanVisit = {
      activeDurationMs: 0,
      activeStartedAt: Date.now(),
      entrySource: entrySource ?? consumeMyPlanEntrySource(),
      interactionCount: 0,
      visitId: createVisitId(),
    };
    visitRef.current = visit;

    trackMixpanelEvent(MixpanelEvents.MY_PLAN_VIEWED, {
      visit_id: visit.visitId,
      entry_source: visit.entrySource,
      selected_day: context.selectedDay,
      selected_date: context.selectedDateKey,
    });
  }, []);

  const trackInteraction = useCallback(
    (interactionType: MyPlanInteractionType, properties: AnalyticsProperties = {}) => {
      const visit = visitRef.current;
      if (!visit) return;

      const context = contextRef.current;
      visit.interactionCount += 1;
      trackMixpanelEvent(MixpanelEvents.MY_PLAN_INTERACTION, {
        visit_id: visit.visitId,
        interaction_type: interactionType,
        selected_day: context.selectedDay,
        selected_date: context.selectedDateKey,
        ...properties,
      });
    },
    [],
  );

  const handleViewWillEnter = useCallback(() => {
    isViewActiveRef.current = true;
    startVisit();
  }, [startVisit]);

  const handleViewDidLeave = useCallback(() => {
    isViewActiveRef.current = false;
    endVisit("route_left");
  }, [endVisit]);

  useIonViewWillEnter(handleViewWillEnter);
  useIonViewDidLeave(handleViewDidLeave);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        endVisit("app_backgrounded");
      } else if (isViewActiveRef.current) {
        startVisit("app_foreground");
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [endVisit, startVisit]);

  useEffect(
    () => () => {
      isViewActiveRef.current = false;
      endVisit("unmounted");
    },
    [endVisit],
  );

  return { trackInteraction };
};

export const MyPlanAnalyticsProvider = ({
  analytics,
  children,
}: PropsWithChildren<{ analytics: MyPlanAnalyticsContextValue }>) => (
  <MyPlanAnalyticsContext.Provider value={analytics}>{children}</MyPlanAnalyticsContext.Provider>
);

/**
 * Meal cards and coach controls are also rendered outside My Plan. Returning
 * null makes their instrumentation a no-op in those contexts.
 */
export const useMyPlanAnalyticsContext = () => useContext(MyPlanAnalyticsContext);
